import { useCallback, useEffect, useMemo, useRef } from 'react';

interface MobileOptimizationsOptions {
  isMobile?: boolean;
  isViewingFiles?: boolean;
  enableVirtualization?: boolean;
}

export const useMobileOptimizations = (options: MobileOptimizationsOptions = {}) => {
  const { isMobile = false, isViewingFiles = false, enableVirtualization = true } = options;
  const scrollTimeoutRef = useRef<number>();
  const isScrollingRef = useRef(false);

  // Debounced scroll handler for better performance
  const handleScrollEnd = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = window.setTimeout(() => {
      isScrollingRef.current = false;
      // Re-enable any disabled optimizations
      if (isMobile) {
        document.body.style.pointerEvents = '';
      }
    }, 150);
  }, [isMobile]);

  const handleScrollStart = useCallback(() => {
    isScrollingRef.current = true;
    // Disable pointer events during scroll for better performance on mobile
    if (isMobile) {
      document.body.style.pointerEvents = 'none';
    }
  }, [isMobile]);

  // Throttled scroll handler
  const throttledScrollHandler = useCallback(
    (event: Event) => {
      if (!isScrollingRef.current) {
        handleScrollStart();
      }
      handleScrollEnd();
    },
    [handleScrollStart, handleScrollEnd],
  );

  // iOS-specific optimizations
  useEffect(() => {
    if (!isMobile) return;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (isIOS || isSafari) {
      // Prevent bounce scrolling on iOS and improve scroll performance
      document.documentElement.style.setProperty('-webkit-overflow-scrolling', 'touch');
      document.documentElement.style.transform = 'translate3d(0,0,0)';
    }

    // Override addEventListener to make scroll listeners passive by default
    const originalAddEventListener = EventTarget.prototype.addEventListener;
    EventTarget.prototype.addEventListener = function (type, listener, options) {
      if (type === 'scroll' || type === 'wheel' || type === 'touchstart' || type === 'touchmove') {
        // Force passive for scroll-related events
        if (typeof options === 'boolean') {
          options = { passive: true, capture: options };
        } else if (typeof options === 'object' && options !== null) {
          options = { ...options, passive: true };
        } else {
          options = { passive: true };
        }
      }
      return originalAddEventListener.call(this, type, listener, options);
    };

    // Add passive listeners for better scroll performance
    const scrollElements = document.querySelectorAll('[data-scroll-container]');
    scrollElements.forEach((element) => {
      element.addEventListener('scroll', throttledScrollHandler, { passive: true });
    });

    return () => {
      // Restore original addEventListener
      EventTarget.prototype.addEventListener = originalAddEventListener;

      scrollElements.forEach((element) => {
        element.removeEventListener('scroll', throttledScrollHandler);
      });

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Reset styles
      document.body.style.pointerEvents = '';
      if (isIOS || isSafari) {
        document.documentElement.style.removeProperty('-webkit-overflow-scrolling');
        document.documentElement.style.transform = '';
      }
    };
  }, [isMobile, throttledScrollHandler]);

  // Memory optimization for large lists
  const getOptimizedItemCount = useCallback(
    (totalItems: number) => {
      if (!isMobile || !enableVirtualization) return totalItems;

      // Limit visible items on mobile for better performance
      const maxMobileItems = 50;
      return Math.min(totalItems, maxMobileItems);
    },
    [isMobile, enableVirtualization],
  );

  // Image loading optimization
  const getImageLoadingStrategy = useCallback(() => {
    if (isMobile) {
      return {
        loading: 'lazy' as const,
        decoding: 'async' as const,
        fetchPriority: 'low' as const,
      };
    }
    return {
      loading: 'eager' as const,
      decoding: 'auto' as const,
      fetchPriority: 'auto' as const,
    };
  }, [isMobile]);

  // Reduce animations on mobile for better performance
  const getAnimationSettings = useCallback(() => {
    if (isMobile) {
      return {
        duration: 150, // Shorter animations
        easing: 'ease-out',
        reduceMotion: true,
      };
    }
    return {
      duration: 300,
      easing: 'ease-in-out',
      reduceMotion: false,
    };
  }, [isMobile]);

  // Optimize touch interactions
  useEffect(() => {
    if (!isMobile) return;

    // Improve touch responsiveness
    const style = document.createElement('style');
    style.textContent = `
      * {
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
      }

      button, a, [role="button"] {
        touch-action: manipulation;
      }

      .scroll-container {
        -webkit-overflow-scrolling: touch;
        overflow-scrolling: touch;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [isMobile]);

  return {
    isScrolling: isScrollingRef.current,
    getOptimizedItemCount,
    getImageLoadingStrategy,
    getAnimationSettings,
    isMobile,
    shouldUseVirtualization: isMobile && enableVirtualization,
  };
};
